import { useEffect, useState } from "react";
import {
  ResponsiveContainer,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Bar,
  BarChart,
  Text,
} from "recharts";
import ReportItem from "../ReportItem";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Skeleton } from "primereact/skeleton";
import Icon from "components/Icon";
import TableBlock from "./TableBlock";

function Wrapper({ children, container, ...other }) {
  if (container === false) return <div {...other}>{children}</div>;
  return <ReportItem {...other}>{children}</ReportItem>;
}

export default function ChartBlock({
  id,
  title,
  description,
  data,
  cols,
  colors = [],
  loading,
  sortField,
  sortOrder,
  footer,
  fields,
  container = true,
  className,
  swap,
  heading,
  desc,
  comp,
}) {
  const chartOptions = {
    indexAxis: "y",
    interaction: {
      intersect: false,
      mode: "nearest",
      axis: "y",
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          display: false,
        },
      },
      x: {
        display: false,
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: "rgba(0,0,0,0)",
        bodyColor: "#000",
        yAlign: "center",
        xAlign: "left",
        callbacks: {
          title: function (context) {
            return "";
          },
        },
      },
    },
  };

  let [barChartData, setBarChartData] = useState({});

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="chart-tooltip">
          <p className="chart-label">{`${payload[0].value}`}</p>
        </div>
      );
    }
  };

  const formatNum = (num) => {
    if (id === "cp_traffic") {
      return Math.round(num).toLocaleString("en-US");
    }
    return num.toLocaleString();
  };

  const sortData = (data, field, order) => {
    if (order === 1) {
      return data.sort((a, b) => a[field] - b[field]);
    } else {
      return data.sort((a, b) => b[field] - a[field]);
    }
  };

  function CustomizedTick(props) {
    const { x, y, stroke, payload } = props;
    return (
      <Text
        x={10}
        y={y}
        style={{ fontSize: "12px" }}
        fill="#000000"
        width={maxLabelWidth}
        textAnchor="left"
        verticalAnchor="middle"
      >
        {payload.value}
      </Text>
    );
  }

  let [maxLabelWidth, setMaxLabelWidth] = useState({});

  useEffect(() => {
    if (data && data.length > 0) {
      let labelWidth = data.reduce((maxWidth, item) => {
        const textWidth = item[fields[0]].length * 8; // Approximate width calculation
        return textWidth > maxWidth ? textWidth : maxWidth;
      }, 0);
      if (labelWidth > 250) {
        setMaxLabelWidth(250);
      } else {
        setMaxLabelWidth(labelWidth);
      }
    }
  }, [data, fields, maxLabelWidth]);

  return (
    <Wrapper id={id} container={container} className={className}>
      <div className="report-item__heading">
        {!heading && <h2 className="report-item__heading-title">{title}</h2>}
        {heading && <h3 className="report-item__heading-subtitle">{title}</h3>}
      </div>
      {description && (
        <p className="report-item__heading-description">{description}</p>
      )}
      <div className="report-item__data showChart">
        <TableBlock
          data={
            comp
              ? data?.filter(
                  (item) =>
                    ![
                      "Competitor 1",
                      "Competitor 2",
                      "Competitor 3",
                      "Competitor 4",
                    ].includes(item.websites)
                )
              : data
          }
          cols={cols}
          colors={colors}
          fields={fields}
          sortField={sortField}
          sortOrder={sortOrder}
          footer={footer}
          loading={loading}
          container={false}
          swap={swap}
          desc={desc}
        />
        {data && (
          <div className="report-item__data-graph">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={sortData(
                  comp
                    ? data?.filter(
                        (item) =>
                          ![
                            "Competitor 1",
                            "Competitor 2",
                            "Competitor 3",
                            "Competitor 4",
                          ].includes(item.websites)
                      )
                    : data,
                  sortField,
                  sortOrder
                )}
                layout="vertical"
                barSize={5}
                margin={{
                  top: 0,
                  right: 70,
                  bottom: 0,
                  left: maxLabelWidth - 55,
                }}
                // margin={{ top: 0, right: 70, bottom: 0, left: 20 }}
                // padding={{ left: 100 }}
              >
                <CartesianGrid stroke="#E5E5E9" horizontal={false} />
                <YAxis
                  type="category"
                  dataKey={fields[0]}
                  tickLine={false}
                  stroke="#000"
                  // padding={{ left: 100 }}
                  // tickFormatter={(value) => {
                  //   return "aaa"
                  // }}
                  tick={<CustomizedTick />}
                />
                <XAxis
                  type="number"
                  dataKey={sortField}
                  tickFormatter={(value) => {
                    if (value >= 1000000000) {
                      return (
                        (value % 1000000000 === 0
                          ? value / 1000000000
                          : (value / 1000000000).toFixed(1)) + "B"
                      );
                    } else if (value >= 1000000) {
                      return (
                        (value % 1000000 === 0
                          ? value / 1000000
                          : (value / 1000000).toFixed(1)) + "M"
                      );
                    } else if (value >= 1000) {
                      return (
                        (value % 1000 === 0
                          ? value / 1000
                          : (value / 1000).toFixed(1)) + "K"
                      );
                    } else {
                      return value.toLocaleString();
                    }
                  }}
                />
                <Bar
                  dataKey={sortField}
                  fill={colors[cols.indexOf(sortField)] ?? "var(--primary)"}
                  radius={[0, 10, 10, 0]}
                  activeBar={{ fill: "#9747FF" }}
                  label={{ position: "right", formatter: formatNum }}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </Wrapper>
  );
}
